import { inject, Injectable } from '@angular/core';

import { CanvasElement, ElementQueryProps, PropsOf } from '@blaze-runtime/engine-domain';
import { ElementQueryState, PaginatedComponentModel } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';

import {
  isElementType,
  isPaginableElement,
} from '../canvas-elements/element-model/element-type-guards';
import { getCalendarRangeQuery } from '../elements/calendar/utils/calendar-query.utils';

import { ElementDefinitionsService } from './element-definitions.service';
import { ViewportService } from './viewport.service';

export const DEFAULT_PAGE_SIZE = 10;
export const PAGINATION_COUNT_OPTIONS = [5, 10, 20, 50, 100, 200];

interface NavigationOptions {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface PaginationProps {
  enabled: boolean;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class ElementPaginationService {
  private elementDefinitionsService = inject(ElementDefinitionsService);
  private schemaService = inject(SchemaService);
  private viewportService = inject(ViewportService);

  getTablePaginationProps(props?: PropsOf<'table'>): PaginationProps {
    const { content } = props || {};
    const { table, pagination } = (content as any) || {};
    const enabled = table?.usePagination ?? false;
    return {
      enabled,
      pageSize: enabled ? pagination?.count || PAGINATION_COUNT_OPTIONS[0] : 0,
    };
  }

  getListPaginationProps(props?: PropsOf<'list'>): PaginationProps {
    const enabled = this.canListElementBeServerPaginated(props);
    return {
      enabled,
      pageSize: this.calculatePageSizeForList(props, enabled),
    };
  }

  applyPaginationStateToModel(
    model: PaginatedComponentModel,
    state: ElementQueryState | undefined,
  ): PaginatedComponentModel {
    return {
      ...model,
      pageIndex: state?.pageIndex || 0,
      pageSize: state?.pageSize || PAGINATION_COUNT_OPTIONS[0],
      state: this.getNavigationOptions(state),
      backwardPaginationSupported: !!state?.backwardPaginationSupported,
      requireRefresh: state?.requireRefresh,
    };
  }

  isMobileListUsingInfinityScroll(props?: PropsOf<'list'>): boolean {
    const mode = props?.content?.list?.layout.direction;
    return this.viewportService.mode === 'mobile' && mode !== 'notification';
  }

  shouldListUseInfinityScroll(props?: PropsOf<'list'>, isServerSidePagination = true): boolean {
    const mode = props?.content?.list?.layout.mode;
    return (
      (this.isMobileListUsingInfinityScroll(props) || mode === 'vscroll' || mode === 'hscroll') &&
      isServerSidePagination
    );
  }

  getPaginationProps(element?: CanvasElement): ElementQueryProps | undefined {
    if (!isPaginableElement(element)) {
      return undefined;
    }

    const definition = this.elementDefinitionsService.getDefinitionSafe(element.type);
    const elementProps = deepmergeExceptArrays(definition.defaults, element.props);

    if (element.type === 'table') {
      const tableProps = elementProps as PropsOf<'table'>;
      const props = this.getTablePaginationProps(tableProps);
      const sort = Array.isArray(tableProps.content?.sort)
        ? tableProps.content?.sort[0]
        : tableProps.content?.sort;
      return {
        ...props,
        sortColumnName: this.schemaService.getColumnName(sort?.field),
        sortDirection: sort?.direction === 'desc' ? 'Descending' : 'Ascending',
      };
    }

    if (element.type === 'list') {
      const listProps = elementProps as PropsOf<'list'>;
      const props = this.getListPaginationProps(listProps);
      const sortConfig = Array.isArray(listProps.content?.sort)
        ? listProps.content.sort[0]
        : listProps.content?.sort;

      return {
        ...props,
        sortColumnName: this.schemaService.getColumnName(sortConfig?.field),
        sortDirection: sortConfig?.direction === 'desc' ? 'Descending' : 'Ascending',
      };
    }

    if (element.type === 'calendar') {
      const calendarProps = elementProps as PropsOf<'calendar'>;
      const props: PaginationProps = {
        enabled: false,
        pageSize: 0,
      };
      return {
        ...props,
        sortColumnName: this.schemaService.getColumnName(calendarProps.content?.input?.titleColumn),
        sortDirection: 'Ascending',
        query: getCalendarRangeQuery(
          calendarProps.content?.input?.startColumn,
          calendarProps.content.input?.endColumn,
        ),
      };
    }

    if (isElementType(element, 'chat')) {
      const chatProps = elementProps as PropsOf<'chat'>;
      const props: PaginationProps = {
        enabled: true,
        pageSize: 20,
      };

      return {
        ...props,
        sortColumnName: this.schemaService.getColumnName(
          chatProps.content.input?.messages.createdTime,
        ),
        sortDirection: 'Descending',
      };
    }

    if (isElementType(element, 'dropdown')) {
      const dropdownProps = elementProps as PropsOf<'dropdown'>;
      const props: PaginationProps = {
        enabled: true,
        pageSize: 20,
      };

      if (!dropdownProps.content.menu.dynamicSearch) {
        return undefined;
      }

      const sortConfig = Array.isArray(dropdownProps.content.sort)
        ? dropdownProps.content.sort[0]
        : dropdownProps.content.sort;
      if (!sortConfig.field) {
        return {
          ...props,
        };
      }

      return {
        ...props,
        sortColumnName: this.schemaService.getColumnName(sortConfig.field),
        sortDirection: sortConfig.direction === 'desc' ? 'Descending' : 'Ascending',
      };
    }

    return undefined;
  }

  private canListElementBeServerPaginated(props?: PropsOf<'list'>): boolean {
    const mode = props?.content?.list?.layout?.mode;
    return (
      this.isMobileListUsingInfinityScroll(props) ||
      mode === 'vscroll' ||
      mode === 'hscroll' ||
      mode === 'paginate' ||
      mode === 'fixed'
    );
  }

  private calculatePageSizeForList(props?: PropsOf<'list'>, isServerSidePagination = true): number {
    const { content } = props || {};
    const { list } = content || {};
    const { layout } = list || {};
    const { direction = 'vertical', mode = 'vscroll', rows = 1, cols = 1 } = layout || {};

    let pageSize = 0;

    if (this.isMobileListUsingInfinityScroll(props)) {
      // Mobile almost always uses virtual scrolling with limited page size
      return DEFAULT_PAGE_SIZE;
    }

    // direction vertical
    if (direction === 'vertical') {
      if (mode === 'hscroll') {
        pageSize = cols;
      } else {
        pageSize = cols * rows;
      }
      // direction horizontal
    } else {
      pageSize = rows;
    }

    if (mode === 'fixed') {
      pageSize = cols * rows;
    }

    if (mode === 'full') {
      pageSize = 0;
    }

    // Fetch more items when dealing with infinite scroll
    if (this.shouldListUseInfinityScroll(props, isServerSidePagination) && pageSize !== 0) {
      return Math.max(20, pageSize * 3);
    }

    return pageSize;
  }

  private getNavigationOptions(state?: ElementQueryState): NavigationOptions {
    if (!state) {
      return { hasPreviousPage: false, hasNextPage: false };
    }

    const { pageIndex, pageSize, totalCount, previousPageToken, nextPageToken } = state;
    const hasPreviousPage = !!previousPageToken && pageSize > 0;
    const hasNextPage = totalCount
      ? (pageIndex + 1) * pageSize < totalCount && !!nextPageToken
      : !!nextPageToken;

    return {
      hasPreviousPage,
      hasNextPage,
    };
  }
}
